import { extendTheme, withDefaultColorScheme } from '@chakra-ui/react';

import { buttonStyleConfig } from './component-styles/button';

// Chakra v2 compliant color scheme: a 50-900 palette derived from the
// primary brand green (#003E32). Use via colorScheme="branded".
const branded = {
  50: '#F0F7F5',
  100: '#ECF3EE',
  200: '#EDF8F4',
  300: '#BED3CD',
  400: '#8EABA3',
  500: '#003E32',
  600: '#1f3833',
  700: '#00352B',
  800: '#002B23',
  900: '#001D17',
};

const colors = {
  green: {
    50: '#F0F7F5',
    100: '#ECF3EE',
    150: '#d1dcda',
    200: '#EDF8F4',
    300: '#BED3CD',
    400: '#8EABA3',
    500: '#003E32',
    600: '#1f3833',
    700: '#00352B',
    800: '#002B23',
    900: '#001D17',
  },
  success: {
    50: '#F0FFF8',
    100: '#C6F7E2',
    200: '#8EEDC7',
    300: '#65D6AD',
    400: '#38C793',
    500: '#00B277',
    600: '#009B68',
    700: '#007855',
    800: '#006347',
    900: '#004D37',
  },
  red: {
    50: '#FFF5F6',
    100: '#FED7DC',
    200: '#FCA5B0',
    300: '#F87385',
    400: '#F24F64',
    500: '#E83A54',
    600: '#BA2E1B',
    700: '#9B2424',
    800: '#7D1D1E',
    900: '#5F1518',
  },
  purple: {
    50: '#F3F0FF',
    100: '#DDD6FE',
    200: '#C4B5FD',
    300: '#A78BFA',
    400: '#8B5CF6',
    500: '#4427B8',
    600: '#3B21A0',
    700: '#321B88',
    800: '#281570',
    900: '#1F1058',
  },
  orange: {
    50: '#FFF8F0',
    100: '#FFECDB',
    200: '#FFD0AA',
    300: '#FFB37A',
    400: '#FF934A',
    500: '#FA6511',
    600: '#D24300',
    700: '#A93600',
    800: '#802900',
    900: '#571C00',
  },
  darkBg: {
    100: '#1B1F26',
    200: '#13161B',
    300: '#1B1F26',
    400: '#0B0D0F',
    500: '#0B0D0F',
    600: '#0B0D0F',
    700: '#0B0D0F',
    800: '#0B0D0F',
    900: '#0B0D0F',
  },
  bg: {
    default: '#0B0D0F',
  },
  border: {
    default: '#1B1F26',
    light: 'rgba(0, 62, 50, 0.2)',
    subtle: '#003E3229',
    subtleAlt: 'rgba(0, 62, 50, 0.16)',
  },
  surface: {
    light: '#1E232A',
    default: '#13161B',
    dark: '#0F1115',
    subtle: '#F6F6F6',
    subtleAlt: '#E1E1E1',
    beige: '#faf8f5',
  },
  macro: {
    // exact design colors
    bgMuted: '#BED3CD',
    progress: {
      high: '#A7441F',
      medium: '#989544',
      low: '#1f3833',
      background: '#BED3CD',
    },
    chip: {
      increaseBg: '#A7441F',
      decreaseBg: '#416D64',
      textMuted: '#878787',
      neutralBg: '#878787',
    },
    palette: {
      calories: '#CD7373',
      carbs: '#CDA173',
      protein: '#73CDA5',
      fat: '#1A6D9F',
      satFat: '#73ABCD',
      fiber: '#7377CD',
      fallback: '#416D64',
    },
    ringStroke: '#D3E2DE',
  },
  text: {
    default: '#FFFFFF',
    muted: 'rgba(195, 208, 229, 0.5)',
    light: 'rgba(224, 236, 255, 0.8)',
  },
  gray: {
    100: '#2D3440',
    200: '#2D3440',
    300: '#2D3440',
    400: '#D9D9D9',
    500: '#2D3440',
    600: '#2D3440',
    700: '#2D3440',
    800: '#2D3440',
    900: '#2D3440',
  },
  branded,
};

export const defaultTheme = extendTheme(
  {
    fonts: {
      heading: 'Canela-Light, serif',
      body: 'Plus Jakarta Sans, sans-serif',
    },
    colors,
    components: {
      Button: buttonStyleConfig,
    },
    semanticTokens: {
      colors: {
        'chakra-body-text': colors.green[500], // #003E32 — brand body text

        // Text - branded green for legacy theme
        'text.default': 'green.500', // #003E32
        'text.muted': 'gray.600', // green.500 at ~70% opacity
        'text.subtle': 'gray.500', // green.500 at ~50% opacity
        'text.inverse': 'white',
        'text.brand': 'green.500', // #003E32
        'text.light': '#003E32CC', // green.500 at ~80% opacity
        'text.disabled': '#003E3280', // green.500 at ~50% opacity
        'text.primary': 'black', // #000000
        'text.secondary': '#6B6B6B', // neutral-600

        // Backgrounds
        'bg.default': 'white',
        'bg.subtle': '#FCFCFC',
        'bg.muted': 'green.100', // #ECF3EE
        'bg.brand': 'green.100', // #ECF3EE
        'bg.inverse': 'green.500', // #003E32

        // Surfaces
        'surface.default': 'white',
        'surface.raised': 'gray.50',
        'surface.overlay': 'white',
        'surface.light': 'gray.50',
        'surface.dark': 'gray.100',
        'surface.subtle': '#F6F6F6',
        'surface.subtleAlt': '#faf8f5',
        'surface.beige': '#faf8f5',

        // Borders
        'border.default': 'gray.200',
        'border.muted': '#718096',
        'border.brand': 'green.500', // #003E32
        'border.light': 'rgba(0, 62, 50, 0.2)',
        'border.subtle': '#003E3229',
        'border.subtleAlt': 'rgba(0, 62, 50, 0.16)',
        'border.divider': '#EFEFEF',

        // Status
        'status.error': 'red.500',
        'status.warning': 'orange.500',
        'status.success': 'success.500',
        'status.info': 'blue.500',

        // Interactive (used by component styles)
        'interactive.brand.default': 'green.500', // #003E32
        'interactive.brand.hover': 'green.600', // #1f3833
        'interactive.brand.active': 'green.700', // #00352B
        'interactive.danger.default': 'red.500',
        'interactive.danger.hover': 'red.600',

        // Provider feedback
        'feedback.banner.bg': 'rgba(0, 62, 50, 0.6)', // green.500 at 60% opacity
        'feedback.card.border': '#e3e8ef',
        'feedback.card.header.bg': '#fcfcfc',
        'feedback.section.label': '#4a4a4a',
        'feedback.strength.title': '#013d31',
        'feedback.experiment.title': '#b85a3a',
        'feedback.suggestion.bg': '#fef8f6',
        'feedback.suggestion.border': '#faf8f5',
        'feedback.suggestion.accent': '#964831',
        'feedback.suggestion.text': '#2c2c2c',
        'feedback.badge.text': '#808080',
      },
    },
    defaultProps: {
      // colorScheme: 'green',
    },
  },
  withDefaultColorScheme({ colorScheme: 'green' }),
);

export const legacyCallTheme = extendTheme(
  {
    fonts: {
      heading: 'Canela-Light, serif',
      body: 'Plus Jakarta Sans, sans-serif',
    },
    colors: {
      ...colors,
      surface: {
        ...colors.surface,
        subtle: '#1E232A',
      },
    },
    components: {
      Button: buttonStyleConfig,
    },
  },
  withDefaultColorScheme({ colorScheme: 'green' }),
);

// Light theme for call pages - uses standard Chakra colors with green accents
export const callV2Theme = extendTheme(
  {
    fonts: {
      heading: 'Plus Jakarta Sans, sans-serif',
      body: 'Plus Jakarta Sans, sans-serif',
    },
    colors: {
      // Keep green accent colors
      green: colors.green,
      success: colors.success,
      // Use standard Chakra gray palette for light mode
      gray: {
        50: '#F7FAFC',
        100: '#EDF2F7',
        200: '#E2E8F0',
        300: '#CBD5E0',
        400: '#A0AEC0',
        500: '#718096',
        600: '#4A5568',
        700: '#2D3748',
        800: '#1A202C',
        900: '#171923',
      },
      // Light mode semantic colors
      bg: {
        default: '#FFFFFF',
      },
      darkBg: {
        100: '#1B1F26',
        200: '#13161B',
        300: '#1B1F26',
        400: '#0B0D0F',
        500: '#0B0D0F',
        600: '#0B0D0F',
        700: '#0B0D0F',
        800: '#0B0D0F',
        900: '#0B0D0F',
      },
      border: {
        default: '#E2E8F0', // gray.200
        light: 'rgba(0, 62, 50, 0.1)',
        subtle: 'rgba(0, 62, 50, 0.15)',
        subtleAlt: 'rgba(0, 62, 50, 0.08)',
      },
      surface: {
        light: '#F7FAFC', // gray.50
        default: '#FFFFFF',
        dark: '#EDF2F7', // gray.100
        subtle: '#F6F6F6',
        subtleAlt: '#faf8f5',
      },
      text: {
        default: '#1A202C', // gray.800
        muted: '#4A5568', // gray.600
        light: '#2D3748', // gray.700
      },
      branded: {
        ...branded,
        light: {
          text: {
            default: colors.green[900],
            muted: colors.green[600],
            light: colors.green[300],
          },
          bg: {
            default: colors.green[100],
            muted: colors.green[200],
            light: colors.green[300],
          },
          border: {
            default: colors.green[400],
          },
        },
      },
      // Keep other colors the same
      purple: colors.purple,
      orange: colors.orange,
      macro: colors.macro,
    },
    components: {
      Button: buttonStyleConfig,
    },
    semanticTokens: {
      colors: {
        // Chakra built-ins
        'chakra-body-text': { default: 'gray.800', _dark: 'whiteAlpha.900' },
        'chakra-body-bg': { default: 'white', _dark: 'gray.800' },
        'chakra-border-color': {
          default: 'gray.200',
          _dark: 'whiteAlpha.300',
        },

        // Text
        'text.default': { default: 'gray.800', _dark: 'whiteAlpha.900' },
        'text.muted': { default: 'gray.600', _dark: 'whiteAlpha.600' },
        'text.subtle': { default: 'gray.500', _dark: 'whiteAlpha.400' },
        'text.inverse': { default: 'white', _dark: 'gray.900' },
        'text.brand': { default: 'green.500', _dark: 'green.300' },
        'text.light': { default: 'gray.700', _dark: 'whiteAlpha.700' }, // alias
        'text.disabled': {
          default: '#003E3280',
          _dark: 'whiteAlpha.400',
        }, // green.500 at ~50% opacity
        'text.primary': { default: 'black', _dark: 'white' },
        'text.secondary': {
          default: '#6B6B6B',
          _dark: 'whiteAlpha.500',
        }, // neutral-600

        // Backgrounds
        'bg.default': { default: 'white', _dark: 'gray.900' },
        'bg.subtle': { default: '#FCFCFC', _dark: 'gray.800' },
        'bg.muted': { default: 'gray.100', _dark: 'gray.700' },
        'bg.brand': { default: 'green.100', _dark: 'green.900' },
        'bg.inverse': { default: 'gray.900', _dark: 'white' },

        // Surfaces
        'surface.default': { default: 'white', _dark: 'gray.800' },
        'surface.raised': { default: 'gray.50', _dark: 'gray.700' },
        'surface.overlay': { default: 'white', _dark: 'gray.700' },
        'surface.light': { default: 'gray.50', _dark: 'gray.700' }, // alias
        'surface.dark': { default: 'gray.100', _dark: 'gray.900' },
        'surface.card': '#FCFCFC', // card backgrounds (lighter than surface.subtle)
        'surface.subtle': '#F6F6F6', // alias (no dark yet)
        'surface.subtleAlt': '#faf8f5', // alias (no dark yet)
        'surface.beige': '#faf8f5', // alias (no dark yet)

        // Borders
        'border.default': {
          default: 'gray.200',
          _dark: 'whiteAlpha.300',
        },
        'border.muted': { default: 'gray.100', _dark: 'whiteAlpha.200' },
        'border.brand': { default: 'green.500', _dark: 'green.400' },
        'border.light': 'rgba(0, 62, 50, 0.1)', // alias
        'border.subtle': 'rgba(0, 62, 50, 0.15)', // alias
        'border.subtleAlt': 'rgba(0, 62, 50, 0.08)', // alias
        'border.divider': '#EFEFEF',

        // Status
        'status.error': { default: 'red.500', _dark: 'red.300' },
        'status.warning': { default: 'orange.500', _dark: 'orange.300' },
        'status.success': { default: 'success.500', _dark: 'success.300' },
        'status.info': { default: 'blue.500', _dark: 'blue.300' },

        // Interactive (used by component styles)
        'interactive.brand.default': {
          default: 'green.500',
          _dark: 'green.400',
        },
        'interactive.brand.hover': {
          default: 'green.600',
          _dark: 'green.300',
        },
        'interactive.brand.active': {
          default: 'green.700',
          _dark: 'green.200',
        },
        'interactive.danger.default': {
          default: 'red.500',
          _dark: 'red.400',
        },
        'interactive.danger.hover': {
          default: 'red.600',
          _dark: 'red.300',
        },

        // Provider feedback
        'feedback.banner.bg': 'rgba(0, 62, 50, 0.6)', // green.500 at 60% opacity
        'feedback.card.border': '#e3e8ef',
        'feedback.card.header.bg': '#fcfcfc',
        'feedback.section.label': '#4a4a4a',
        'feedback.strength.title': '#013d31',
        'feedback.experiment.title': '#b85a3a',
        'feedback.suggestion.bg': '#fef8f6',
        'feedback.suggestion.border': '#faf8f5',
        'feedback.suggestion.accent': '#964831',
        'feedback.suggestion.text': '#2c2c2c',
        'feedback.badge.text': '#808080',
      },
    },
    defaultProps: {
      colorScheme: 'green', // Use green as accent
    },
    config: {
      initialColorMode: 'light',
      useSystemColorMode: false,
    },
  },
  withDefaultColorScheme({ colorScheme: 'green' }),
);

export default defaultTheme;
